import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import GenericProductAttributeSelector from '../GenericProductAttributeSelector';
import ProductAttributeSelector, {
  useProductAttributeSelectorContext,
} from '../ProductAttributeSelector';
import { parseBraSize, parseBraSizes } from '../utils/sizes';

const BraSizeAttributeSelectorWrapper = styled.div`
  ${({ theme }) => theme.selectorStyles.braSizeAttributeSelectorWrapper};
`;

export default function BraSizeAttributeSelector({
  radioButtonProps,
  renderBandSizeSelector,
  renderCupSizeSelector,
  renderBandSummary,
  renderCupSummary,
  ...rest
}) {
  const { options, selectedOption, onChange } =
    useProductAttributeSelectorContext();

  let selectedBandSizeFromContext;
  let selectedCupSizeFromContext;

  if (selectedOption) {
    [selectedBandSizeFromContext, selectedCupSizeFromContext] = parseBraSize(
      selectedOption.label
    );
  }

  const [selectedBandSizeFromState, setSelectedBandSizeFromState] = useState(
    selectedBandSizeFromContext || null
  );
  const [selectedCupSizeFromState, setSelectedCupSizeFromState] = useState(
    selectedCupSizeFromContext || null
  );

  const cupSizesByBand = useMemo(() => {
    return parseBraSizes({
      braSizes: options.map((option) => option.label),
      ignoreInvalidSizes: true,
    });
  }, [options]);

  // It's possible that something other than this particular component could
  // change the selected attribute in the context provider above. In that case,
  // we need to update the internal state of this component to match; we never
  // want a case where the UI disagrees with what is actually selected,
  // otherwise people won't be selecting the product they think they are.
  const selectedBandSize =
    selectedBandSizeFromContext || selectedBandSizeFromState;
  const selectedCupSize =
    selectedCupSizeFromContext || selectedCupSizeFromState;

  // Keep internal state synced with changes from attribute context.
  useEffect(() => {
    if (selectedBandSizeFromContext) {
      setSelectedBandSizeFromState(selectedBandSizeFromContext);
    }
    if (selectedCupSizeFromContext) {
      setSelectedCupSizeFromState(selectedCupSizeFromContext);
    }
  }, [selectedBandSizeFromContext, selectedCupSizeFromContext]);

  const bandSizeContext = useMemo(() => {
    const bandOptions = Object.keys(cupSizesByBand).map((bandSize) => {
      const isAvailable = selectedCupSize
        ? options.some(
            (option) =>
              option.label === `${bandSize}${selectedCupSize}` &&
              option.isAvailable
          )
        : options.some(
            (option) =>
              parseBraSize(option.label)[0] === bandSize && option.isAvailable
          );
      return {
        label: bandSize,
        value: bandSize,
        isAvailable,
        isAvailableForUser: isAvailable,
      };
    });
    return {
      attribute: { field: 'size_bra_band', label: 'Band Size' },
      options: bandOptions,
      selectedOption: bandOptions.find(
        (option) => option.label === selectedBandSize
      ),
      onChange: (bandOption) => {
        setSelectedBandSizeFromState(bandOption.label);
        if (selectedCupSize) {
          const selectedSize = `${bandOption.label}${selectedCupSize}`;
          const option = options.find(
            (option) => option.label === selectedSize
          );
          if (option) {
            onChange(option);
          } else {
            onChange(null);
          }
        }
      },
    };
  }, [cupSizesByBand, onChange, options, selectedBandSize, selectedCupSize]);

  const cupSizeContext = useMemo(() => {
    const uniqueOptions = new Set([].concat(...Object.values(cupSizesByBand)));
    const cupOptions = Array.from(uniqueOptions).map((cupSize) => {
      const isAvailable = selectedBandSize
        ? options.some(
            (option) =>
              option.label === `${selectedBandSize}${cupSize}` &&
              option.isAvailable
          )
        : options.some(
            (option) =>
              parseBraSize(option.label)[1] === cupSize && option.isAvailable
          );
      return {
        label: cupSize,
        value: cupSize,
        isAvailable,
        isAvailableForUser: isAvailable,
      };
    });
    return {
      attribute: { field: 'size_bra_cup', label: 'Cup Size' },
      options: cupOptions,
      selectedOption: cupOptions.find(
        (option) => option.label === selectedCupSize
      ),
      onChange: (cupOption) => {
        setSelectedCupSizeFromState(cupOption.label);
        if (selectedBandSize) {
          const selectedSize = `${selectedBandSize}${cupOption.label}`;
          const option = options.find(
            (option) => option.label === selectedSize
          );
          if (option) {
            onChange(option);
          } else {
            onChange(null);
          }
        }
      },
    };
  }, [cupSizesByBand, onChange, options, selectedBandSize, selectedCupSize]);

  const bandSelectorRadioButtonProps = useMemo(
    () => ({
      'data-autotag': 'selector-band-size',
      ...radioButtonProps,
    }),
    [radioButtonProps]
  );

  const cupSelectorRadioButtonProps = useMemo(
    () => ({
      'data-autotag': 'selector-cup-size',
      ...radioButtonProps,
    }),
    [radioButtonProps]
  );

  return (
    <BraSizeAttributeSelectorWrapper>
      <ProductAttributeSelector.Context.Provider value={bandSizeContext}>
        {renderBandSizeSelector ? (
          renderBandSizeSelector()
        ) : (
          <GenericProductAttributeSelector
            {...rest}
            renderSummary={renderBandSummary}
            radioButtonProps={bandSelectorRadioButtonProps}
          />
        )}
      </ProductAttributeSelector.Context.Provider>
      <ProductAttributeSelector.Context.Provider value={cupSizeContext}>
        {renderCupSizeSelector ? (
          renderCupSizeSelector()
        ) : (
          <GenericProductAttributeSelector
            {...rest}
            renderSummary={renderCupSummary}
            radioButtonProps={cupSelectorRadioButtonProps}
          />
        )}
      </ProductAttributeSelector.Context.Provider>
    </BraSizeAttributeSelectorWrapper>
  );
}

BraSizeAttributeSelector.propTypes = {
  /**
   * Props passed to the radio button inside of the selector.
   */
  radioButtonProps: PropTypes.object,
  /**
   * A custom function for rendering the band size. If not provided, it defaults
   * to rendering a `GenericProductAttributeSelector`. Your custom component
   * should consume the selected state via `useProductAttributeSelectorContext`.
   */
  renderBandSizeSelector: PropTypes.func,
  /**
   * A custom function for rendering the cup summary.
   */
  renderBandSummary: PropTypes.func,
  /**
   * A custom function for rendering the cup size. If not provided, it defaults
   * to rendering a `GenericProductAttributeSelector`. Your custom component
   * should consume the selected state via `useProductAttributeSelectorContext`.
   */
  renderCupSizeSelector: PropTypes.func,
  /**
   * A custom function for rendering the cup summary.
   */
  renderCupSummary: PropTypes.func,
};
